import { useMemo, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Layers,
  Coffee,
  BarChart3,
  X,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { PageLoader } from "@/components/ui/Spinner";
import { Button } from "@/components/ui/Button";
import { formatAmount } from "@/utils/formatAmount";
import {
  OccupancyChart,
  ALLOC_COLOR,
  FREE_COLOR,
  NONPROJ_COLOR,
} from "@/features/finances/components/OccupancyChart";
import { ForecastGridEditor } from "@/features/finances/components/ForecastGridEditor";
import {
  useGroupForecast,
  useSetGroupForecast,
  useSetEmployeeProjectStatus,
} from "@/features/finances/hooks/useGroupForecast";
import type {
  ForecastCell,
  ForecastEmployeeRow,
  ForecastGroupRef,
  ForecastSetEntry,
  GroupForecastSummary,
} from "@/features/finances/types/groupForecast";

const MONTHS_SHORT = [
  "Sty",
  "Lut",
  "Mar",
  "Kwi",
  "Maj",
  "Cze",
  "Lip",
  "Sie",
  "Wrz",
  "Paź",
  "Lis",
  "Gru",
];
// Paleta kolorów grup/przedsięwzięć — dobrana tak, by sąsiednie indeksy były
// kontrastowe, a tekst etykiet (biały) był czytelny na każdym tle.
const GROUP_COLORS = [
  "#2563eb",
  "#16a34a",
  "#d97706",
  "#9333ea",
  "#dc2626",
  "#0891b2",
  "#ca8a04",
  "#db2777",
  "#4f46e5",
  "#65a30d",
  "#0d9488",
  "#e11d48",
  "#7c3aed",
  "#ea580c",
  "#0284c7",
  "#15803d",
  "#b45309",
  "#6d28d9",
];

const pct = (f: number) => Math.round(f * 100);
const mkey = (y: number, m: number) => `${y}-${m}`;

type ViewMode = "charts" | "groups" | "free";

export function GroupForecastPage() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const horizon = 12;
  const { data, isLoading } = useGroupForecast(year, month, horizon);
  const [view, setView] = useState<ViewMode>("charts");
  const [edit, setEdit] = useState<{
    employee: ForecastEmployeeRow;
    year: number;
    month: number;
    cell: ForecastCell;
    prevFacts: Record<string, number>;
  } | null>(null);

  // Kolor zależny od pozycji grupy na liście wszystkich grup (stabilny i ROZRÓŻNIALNY),
  // a nie od hasha UUID (kolizje → wszystko jedną barwą). Dzięki temu ta sama grupa
  // ma ten sam kolor na wykresie każdego pracownika i w zakładce „Widok zlecenia".
  const colorFor = useMemo(() => {
    const map = new Map<string, string>();
    (data?.groups ?? []).forEach((g, i) => {
      map.set(g.id, GROUP_COLORS[i % GROUP_COLORS.length]);
    });
    return (gid: string | null): string =>
      (gid != null ? map.get(gid) : undefined) ?? ALLOC_COLOR;
  }, [data]);

  function shift(delta: number) {
    let ny = year,
      nm = month + delta;
    if (nm < 1) {
      ny--;
      nm = 12;
    }
    if (nm > 12) {
      ny++;
      nm = 1;
    }
    setYear(ny);
    setMonth(nm);
  }

  function openEdit(employee: ForecastEmployeeRow, cell: ForecastCell) {
    // referencja: ostatni miesiąc PRZED edytowanym z danymi (fakt/prognoza) per grupa
    const idx = employee.by_month.findIndex(
      (c) => c.year === cell.year && c.month === cell.month,
    );
    const prevFacts: Record<string, number> = {};
    if (idx > 0) {
      for (const seg of employee.by_month[idx - 1].segments) {
        if (seg.group_id) prevFacts[seg.group_id] = seg.fraction;
      }
    }
    setEdit({ employee, year: cell.year, month: cell.month, cell, prevFacts });
  }

  if (isLoading || !data) return <PageLoader />;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-xl lg:text-2xl font-bold text-gray-900 dark:text-gray-100">
            Prognoza obłożenia
          </h1>
          <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1 mb-2" />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Obłożenie pracowników per grupa zleceń — dane z systemu uwzględniane
            do bieżącego miesiąca, dalej przyjęta prognoza.
          </p>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => shift(-1)}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"
          >
            <ChevronLeft size={18} />
          </button>
          <span className="px-3 font-semibold text-sm text-gray-800 dark:text-gray-100 min-w-[120px] text-center">
            od {MONTHS_SHORT[month - 1]} {year}
          </span>
          <button
            onClick={() => shift(1)}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>

      {/* Zakładki */}
      <div className="flex gap-1 border-b border-gray-200 dark:border-gray-700">
        {(
          [
            {
              id: "charts",
              label: "Wykresy obłożenia",
              icon: <BarChart3 size={14} />,
            },
            {
              id: "groups",
              label: "Widok zlecenia",
              icon: <Layers size={14} />,
            },
            { id: "free", label: "Wolne moce", icon: <Coffee size={14} /> },
          ] as { id: ViewMode; label: string; icon: React.ReactNode }[]
        ).map((t) => (
          <button
            key={t.id}
            onClick={() => setView(t.id)}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              view === t.id
                ? "border-[var(--color-primary)] text-[var(--color-primary)]"
                : "border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
            }`}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {view === "charts" && (
        <>
          <ChartsView data={data} colorFor={colorFor} onEditCell={openEdit} />
          <EmployeeStatusPanel data={data} />
        </>
      )}
      {view === "groups" && <GroupsTable data={data} colorFor={colorFor} />}
      {view === "free" && <FreeTable data={data} />}

      {edit && (
        <ForecastEditModal
          groups={data.groups}
          employee={edit.employee}
          year={edit.year}
          month={edit.month}
          cell={edit.cell}
          prevFacts={edit.prevFacts}
          onClose={() => setEdit(null)}
        />
      )}
    </div>
  );
}


/* ── Panel statusu: kto jest projektowy / nieprojektowy w którym miesiącu (zmienne w czasie) ── */
function EmployeeStatusPanel({ data }: { data: GroupForecastSummary }) {
  const setStatus = useSetEmployeeProjectStatus();
  const [open, setOpen] = useState(false);
  // pending[empId]["y-m"] = nowy status (true=nieprojektowy, false=projektowy). Tylko zmienione.
  const [pending, setPending] = useState<
    Record<string, Record<string, boolean>>
  >({});

  // Status bazowy z danych (efektywny w danym miesiącu).
  const baseStatus = useMemo(() => {
    const m: Record<string, Record<string, boolean>> = {};
    for (const emp of data.employees) {
      m[emp.employee_id] = {};
      for (const c of emp.by_month)
        m[emp.employee_id][mkey(c.year, c.month)] = c.is_non_project;
    }
    return m;
  }, [data.employees]);

  const effective = (empId: string, k: string) => {
    const p = pending[empId]?.[k];
    return p !== undefined ? p : (baseStatus[empId]?.[k] ?? false);
  };

  function toggle(empId: string, year: number, month: number) {
    const k = mkey(year, month);
    const next = !effective(empId, k);
    setPending((prev) => {
      const empMap = { ...(prev[empId] ?? {}) };
      // jeśli wracamy do bazowego — usuń z pending; inaczej zapisz
      if (next === (baseStatus[empId]?.[k] ?? false)) delete empMap[k];
      else empMap[k] = next;
      const out = { ...prev, [empId]: empMap };
      if (Object.keys(empMap).length === 0) delete out[empId];
      return out;
    });
  }

  const changeCount = Object.values(pending).reduce(
    (s, m) => s + Object.keys(m).length,
    0,
  );

  function save() {
    const entries = [] as {
      employee: string;
      year: number;
      month: number;
      is_non_project: boolean;
    }[];
    for (const [empId, months] of Object.entries(pending)) {
      for (const [k, val] of Object.entries(months)) {
        const [y, mo] = k.split("-").map(Number);
        entries.push({
          employee: empId,
          year: y,
          month: mo,
          is_non_project: val,
        });
      }
    }
    if (entries.length === 0) return;
    setStatus.mutate(entries, { onSuccess: () => setPending({}) });
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg mt-2">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
      >
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Status: projektowy / nieprojektowy
          </h3>
          <p className="text-xs text-gray-500">
            Kto liczy się imiennie na zlecenia (<b>projektowy</b>), a czyj koszt
            rozkłada się na wszystkie grupy (<b>nieprojektowy</b> — kadry,
            księgowość, dokumenty). Można mieszać okresy: ten sam człowiek
            nieprojektowy w jednych miesiącach, projektowy w innych. Kliknij
            komórkę, by zmienić.
          </p>
        </div>
        <span className="text-gray-400 text-xs shrink-0 ml-3">
          {open ? "Zwiń ▲" : "Rozwiń ▼"}
        </span>
      </button>

      {open && (
        <div className="px-2 pb-3">
          <div className="overflow-x-auto">
            <table className="text-xs border-collapse">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="sticky left-0 bg-white dark:bg-gray-800 text-left px-3 py-2 font-mono uppercase tracking-wider text-gray-500 min-w-[160px]">
                    Pracownik
                  </th>
                  {data.months.map((m) => (
                    <th
                      key={mkey(m.year, m.month)}
                      className="px-1.5 py-2 font-mono text-gray-500 dark:text-gray-400 min-w-[52px] text-center"
                    >
                      {MONTHS_SHORT[m.month - 1]}
                      <div className="text-[10px] opacity-50">
                        {String(m.year).slice(2)}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.employees.map((emp) => (
                  <tr
                    key={emp.employee_id}
                    className="border-b border-gray-100 dark:border-gray-700/50"
                  >
                    <td className="sticky left-0 bg-white dark:bg-gray-800 px-3 py-1 text-gray-900 dark:text-gray-100 truncate max-w-[160px]">
                      {emp.employee_name}
                    </td>
                    {data.months.map((m) => {
                      const k = mkey(m.year, m.month);
                      const np = effective(emp.employee_id, k);
                      const changed =
                        pending[emp.employee_id]?.[k] !== undefined;
                      return (
                        <td key={k} className="px-0.5 py-1 text-center">
                          <button
                            onClick={() =>
                              toggle(emp.employee_id, m.year, m.month)
                            }
                            title={
                              np
                                ? "Nieprojektowy — koszt rozdzielony. Kliknij, by uczynić projektowym."
                                : "Projektowy — koszt imienny. Kliknij, by uczynić nieprojektowym."
                            }
                            className={`w-7 h-6 rounded text-[11px] font-bold transition ${
                              np
                                ? "bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-300"
                                : "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-200"
                            } ${changed ? "ring-2 ring-blue-500" : ""}`}
                          >
                            {np ? "N" : "P"}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between gap-3 px-2 pt-3">
            <div className="flex items-center gap-3 text-[11px] text-gray-500">
              <span className="flex items-center gap-1">
                <span className="inline-block w-4 h-4 rounded bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 text-center leading-4 font-bold">
                  P
                </span>{" "}
                projektowy (imiennie)
              </span>
              <span className="flex items-center gap-1">
                <span className="inline-block w-4 h-4 rounded bg-gray-200 dark:bg-gray-600 text-gray-700 text-center leading-4 font-bold">
                  N
                </span>{" "}
                nieprojektowy
              </span>
            </div>
            <div className="flex items-center gap-2">
              {changeCount > 0 && (
                <span className="text-xs text-blue-600 dark:text-blue-400">
                  {changeCount} zmian
                </span>
              )}
              <Button
                size="sm"
                variant="secondary"
                disabled={changeCount === 0 || setStatus.isPending}
                onClick={() => setPending({})}
              >
                Cofnij
              </Button>
              <Button
                size="sm"
                loading={setStatus.isPending}
                disabled={changeCount === 0}
                onClick={save}
              >
                Zapisz status
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Wykresy obłożenia: słupek na człowieka + multicheck + filtr + megawykres KWOT ── */
function ChartsView({
  data,
  colorFor,
  onEditCell,
}: {
  data: GroupForecastSummary;
  colorFor: (gid: string | null) => string;
  onEditCell: (emp: ForecastEmployeeRow, cell: ForecastCell) => void;
}) {
  // wszyscy z jakimkolwiek kosztem/obłożeniem LUB statusem nieprojektowym (pokażemy ich na czarno,
  // nawet bez ustawionej pensji — inaczej znikają z wykresów)
  const people = data.employees.filter((e) =>
    e.by_month.some(
      (c) =>
        c.segments.length > 0 ||
        c.free_pln > 0.005 ||
        c.monthly_cost > 0 ||
        c.is_non_project,
    ),
  );

  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(people.map((p) => p.employee_id)),
  );

  // nazwy grup, na których pracuje dana osoba (do filtra po przedsięwzięciu/grupie)
  const groupNames = (emp: ForecastEmployeeRow) => {
    const s = new Set<string>();
    for (const c of emp.by_month)
      for (const seg of c.segments) s.add(seg.group_name.toLowerCase());
    return [...s];
  };
  const q = query.trim().toLowerCase();
  const filtered = q
    ? people.filter(
        (e) =>
          e.employee_name.toLowerCase().includes(q) ||
          groupNames(e).some((n) => n.includes(q)),
      )
    : people;

  function toggle(id: string) {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }
  const selectAll = () =>
    setSelected(new Set(filtered.map((p) => p.employee_id)));
  const deselectAll = () => setSelected(new Set());

  const sel = filtered.filter((p) => selected.has(p.employee_id));
  const mega = data.months.map((m) => {
    let alloc = 0,
      free = 0,
      nonproj = 0;
    for (const emp of sel) {
      const c = emp.by_month.find(
        (x) => x.year === m.year && x.month === m.month,
      );
      if (!c) continue;
      if (c.is_non_project) nonproj += c.monthly_cost;
      else {
        alloc += c.allocated_pln;
        free += c.free_pln;
      }
    }
    return {
      label: `${MONTHS_SHORT[m.month - 1]} ${String(m.year).slice(2)}`,
      przydzielone: Math.round(alloc),
      wolne: Math.round(free),
      nieprojektowe: Math.round(nonproj),
    };
  });
  const totAlloc = mega.reduce((s, r) => s + r.przydzielone, 0);
  const totFree = mega.reduce((s, r) => s + r.wolne, 0);
  const totNonproj = mega.reduce((s, r) => s + r.nieprojektowe, 0);
  const grand = totAlloc + totFree + totNonproj;
  const allocPct = grand > 0 ? Math.round((totAlloc / grand) * 100) : 0;

  if (people.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 py-12 text-center text-sm text-gray-400 rounded-lg">
        Brak przydziałów do pokazania na wykresie.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Pasek narzędzi: filtr + zaznacz/odznacz */}
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Filtruj po imieniu lub grupie…"
          className="text-sm border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1.5 w-64 bg-white dark:bg-gray-800 dark:text-gray-100"
        />
        <Button size="sm" variant="secondary" onClick={selectAll}>
          Zaznacz wszystko
        </Button>
        <Button size="sm" variant="secondary" onClick={deselectAll}>
          Odznacz wszystko
        </Button>
        <span className="text-xs text-gray-400 ml-auto">
          Zaznaczeni: {sel.length}/{filtered.length}. Klik
          przyszłego/edytowalnego słupka = edycja prognozy.
        </span>
      </div>
      {/* Legenda kolorów */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-gray-500">
        <span className="flex items-center gap-1.5">
          <span
            className="inline-block w-3 h-3 rounded-sm"
            style={{
              background:
                "linear-gradient(135deg,#2563eb 0 33%,#16a34a 33% 66%,#d97706 66% 100%)",
            }}
          />{" "}
          przydzielone (kolor = przedsięwzięcie)
        </span>
        <span className="flex items-center gap-1.5">
          <span
            className="inline-block w-3 h-3 rounded-sm"
            style={{ background: FREE_COLOR }}
          />{" "}
          wolne moce
        </span>
        <span className="flex items-center gap-1.5">
          <span
            className="inline-block w-3 h-3 rounded-sm"
            style={{ background: NONPROJ_COLOR }}
          />{" "}
          nieprojektowy
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 rounded-sm bg-[repeating-linear-gradient(45deg,#94a3b8,#94a3b8_2px,#fff_2px,#fff_4px)] border border-gray-300" />{" "}
          przydział pracownika (zakreskowane)
        </span>
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 py-10 text-center text-sm text-gray-400 rounded-lg">
          Brak osób pasujących do „{query}".
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {filtered.map((emp) => (
            <div
              key={emp.employee_id}
              className={`bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700 ${selected.has(emp.employee_id) ? "ring-1 ring-[var(--color-primary)]/40" : "opacity-70"}`}
            >
              <label className="flex items-center gap-2 mb-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selected.has(emp.employee_id)}
                  onChange={() => toggle(emp.employee_id)}
                  className="w-4 h-4 rounded"
                />
                <span className="font-semibold text-sm text-gray-900 dark:text-gray-100 truncate">
                  {emp.employee_name}
                </span>
                {(() => {
                  const cost = Math.max(
                    0,
                    ...emp.by_month.map((c) => c.monthly_cost),
                  );
                  return cost > 0 ? (
                    <span
                      className="ml-auto text-xs tabular-nums text-gray-500 dark:text-gray-400 shrink-0"
                      title="Miesięczny koszt z bazy (EmployeeSalaryCost)"
                    >
                      {formatAmount(cost.toFixed(2))} zł/mc
                    </span>
                  ) : (
                    <span
                      className="ml-auto text-xs text-amber-600 dark:text-amber-400 shrink-0"
                      title="Brak EmployeeSalaryCost dla tej osoby"
                    >
                      ⚠ brak pensji
                    </span>
                  );
                })()}
              </label>
              <OccupancyChart
                emp={emp}
                groups={data.groups}
                colorFor={colorFor}
                onEditCell={(cell) => onEditCell(emp, cell)}
              />
            </div>
          ))}
        </div>
      )}

      {/* Megawykres: łączne kwoty dla zaznaczonych (czarne i szare na samej górze) */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mb-3">
          <h3 className="text-sm font-mono uppercase tracking-wider text-gray-500">
            Zaznaczeni razem ({sel.length})
          </h3>
          <span className="text-sm">
            Przydzielone:{" "}
            <b style={{ color: ALLOC_COLOR }}>
              {formatAmount(totAlloc.toFixed(2))} PLN
            </b>{" "}
            <span className="text-gray-400">({allocPct}% płac)</span>
          </span>
          <span className="text-sm">
            Wolne:{" "}
            <b className="text-gray-500 dark:text-gray-400">
              {formatAmount(totFree.toFixed(2))} PLN
            </b>
          </span>
          <span className="text-sm">
            Nieprojektowe:{" "}
            <b className="text-gray-900 dark:text-gray-100">
              {formatAmount(totNonproj.toFixed(2))} PLN
            </b>
          </span>
        </div>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart
            data={mega}
            margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              stroke="#e5e7eb"
              opacity={0.4}
            />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 10 }}
              interval={0}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 10 }}
              tickLine={false}
              axisLine={false}
              width={64}
            />
            <Tooltip
              contentStyle={{ fontSize: 12, borderRadius: 8 }}
              formatter={(value) =>
                `${formatAmount(Number(value).toFixed(2))} PLN`
              }
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar
              dataKey="przydzielone"
              stackId="a"
              name="Przydzielone płace (zł)"
              fill={ALLOC_COLOR}
            />
            <Bar
              dataKey="wolne"
              stackId="a"
              name="Wolne moce (zł)"
              fill={FREE_COLOR}
            />
            <Bar
              dataKey="nieprojektowe"
              stackId="a"
              name="Nieprojektowe (zł)"
              fill={NONPROJ_COLOR}
              radius={[3, 3, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

type GroupCell = {
  et: number;
  pln: number;
  people: { name: string; fraction: number; pln: number }[];
};

/* ── Widok zlecenia: grupy × miesiące, KOSZT (zł) na grupę; klik → skąd wynik ── */
function GroupsTable({
  data,
  colorFor,
}: {
  data: GroupForecastSummary;
  colorFor: (gid: string | null) => string;
}) {
  const agg: Record<string, Record<string, GroupCell>> = {};
  for (const emp of data.employees) {
    for (const cell of emp.by_month) {
      const k = mkey(cell.year, cell.month);
      for (const seg of cell.segments) {
        if (!seg.group_id) continue;
        (agg[seg.group_id] ??= {})[k] ??= { et: 0, pln: 0, people: [] };
        const c = agg[seg.group_id][k];
        c.et += seg.fraction;
        c.pln += seg.pln;
        c.people.push({
          name: emp.employee_name,
          fraction: seg.fraction,
          pln: seg.pln,
        });
      }
    }
  }
  const monthLabel = (m: { year: number; month: number }) =>
    `${MONTHS_SHORT[m.month - 1]} ${m.year}`;
  const [drill, setDrill] = useState<{
    gname: string;
    mlabel: string;
    cell: GroupCell;
  } | null>(null);

  return (
    <>
      <div className="bg-white dark:bg-gray-800 overflow-x-auto">
        <p className="text-xs text-gray-500 px-3 pt-3">
          Koszt płac per grupa zleceń i miesiąc (zł). Kliknij komórkę aby
          sprawdzić dokładny rozkład
        </p>
        <table className="text-xs border-collapse mt-2">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-700">
              <th className="sticky left-0 bg-white dark:bg-gray-800 text-left px-3 py-2 font-mono uppercase tracking-wider text-gray-500 min-w-[160px]">
                Zlecenie / grupa
              </th>
              {data.months.map((m) => (
                <th
                  key={mkey(m.year, m.month)}
                  className="px-2 py-2 font-mono text-gray-500 dark:text-gray-400 min-w-[72px] text-center"
                >
                  {MONTHS_SHORT[m.month - 1]}
                  <div className="text-[10px] opacity-50">
                    {String(m.year).slice(2)}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.groups.map((g) => (
              <tr
                key={g.id}
                className="border-b border-gray-100 dark:border-gray-700/50"
              >
                <td className="sticky left-0 bg-white dark:bg-gray-800 px-3 py-1.5 text-gray-900 dark:text-gray-100 truncate max-w-[160px]">
                  <span
                    className="inline-block w-2 h-2 rounded-full mr-1.5 align-middle"
                    style={{ backgroundColor: colorFor(g.id) }}
                  />
                  {g.short_name || g.name}
                </td>
                {data.months.map((m) => {
                  const c = agg[g.id]?.[mkey(m.year, m.month)];
                  if (!c || c.pln < 0.005) {
                    return (
                      <td
                        key={mkey(m.year, m.month)}
                        className="px-2 py-1.5 text-center"
                      >
                        <span className="opacity-20">—</span>
                      </td>
                    );
                  }
                  return (
                    <td
                      key={mkey(m.year, m.month)}
                      className="px-1 py-1 text-center"
                    >
                      <button
                        onClick={() =>
                          setDrill({
                            gname: g.name,
                            mlabel: monthLabel(m),
                            cell: c,
                          })
                        }
                        title={c.people.map((p) => p.name).join(", ")}
                        className="w-full rounded px-1 py-0.5 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                      >
                        <div className="tabular-nums font-medium text-gray-900 dark:text-gray-100">
                          {formatAmount(c.pln.toFixed(2))}
                        </div>
                        <div className="text-[9px] text-gray-400">
                          ({c.people.length} os. — {c.et.toFixed(1)} et)
                        </div>
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {drill && (
        <GroupDrilldownModal {...drill} onClose={() => setDrill(null)} />
      )}
    </>
  );
}

function GroupDrilldownModal({
  gname,
  mlabel,
  cell,
  onClose,
}: {
  gname: string;
  mlabel: string;
  cell: GroupCell;
  onClose: () => void;
}) {
  const people = [...cell.people].sort((a, b) => b.pln - a.pln);
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-5 w-[32rem] max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="font-bold text-gray-900 dark:text-gray-100 truncate">
              {gname}
            </h2>
            <p className="text-xs text-gray-500">{mlabel}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 shrink-0"
          >
            <X size={18} />
          </button>
        </div>

        {/* Podsumowanie */}
        <div className="grid grid-cols-3 gap-2 my-4">
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-gray-400">
              Koszt płac
            </div>
            <div className="text-base font-bold tabular-nums text-gray-900 dark:text-gray-100">
              {formatAmount(cell.pln.toFixed(2))}{" "}
              <span className="text-xs font-normal text-gray-400">PLN</span>
            </div>
          </div>
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-gray-400">
              Osoby
            </div>
            <div className="text-base font-bold tabular-nums text-gray-900 dark:text-gray-100">
              {cell.people.length}
            </div>
          </div>
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-gray-400">
              Etaty
            </div>
            <div className="text-base font-bold tabular-nums text-gray-900 dark:text-gray-100">
              {cell.et.toFixed(2)}{" "}
              <span className="text-xs font-normal text-gray-400">et</span>
            </div>
          </div>
        </div>

        <h3 className="text-xs font-mono uppercase tracking-wider text-gray-500 mb-2">
          Rozkład na czynniki
        </h3>
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-gray-400 bg-gray-50 dark:bg-gray-800/60 border-b border-gray-200 dark:border-gray-700">
                <th className="px-3 py-2 font-medium">Osoba</th>
                <th className="px-3 py-2 font-medium w-40">Udział etatu</th>
                <th className="px-3 py-2 font-medium text-right">Kwota</th>
              </tr>
            </thead>
            <tbody>
              {people.map((p, i) => (
                <tr
                  key={i}
                  className="border-b border-gray-50 dark:border-gray-700/50 last:border-0 hover:bg-gray-50 dark:hover:bg-gray-700/30"
                >
                  <td className="px-3 py-2 text-gray-800 dark:text-gray-100">
                    {p.name}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-[var(--color-primary)]"
                          style={{
                            width: `${Math.min(pct(p.fraction), 100)}%`,
                          }}
                        />
                      </div>
                      <span className="tabular-nums text-xs text-gray-500 w-10 text-right">
                        {pct(p.fraction)}%
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-900 dark:text-gray-100">
                    {formatAmount(p.pln.toFixed(2))}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="font-bold text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-gray-800/60 border-t border-gray-200 dark:border-gray-700">
                <td className="px-3 py-2">Razem ({cell.people.length} os.)</td>
                <td className="px-3 py-2 tabular-nums text-gray-600 dark:text-gray-300">
                  {pct(cell.et)}%
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {formatAmount(cell.pln.toFixed(2))} PLN
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ── Wolne moce: ludzie z niedociążeniem × miesiące — KWOTY zł (niedociążenie × koszt) ── */
function FreeTable({ data }: { data: GroupForecastSummary }) {
  const monthKeys = data.months.map((m) => mkey(m.year, m.month));
  const freePlnByMonth: Record<string, number> = {};
  for (const emp of data.employees) {
    for (const cell of emp.by_month) {
      // komórki nieprojektowe mają free_pln=0, więc sumowanie wszystkich jest poprawne
      const k = mkey(cell.year, cell.month);
      freePlnByMonth[k] = (freePlnByMonth[k] || 0) + cell.free_pln;
    }
  }
  const grandTotal = Object.values(freePlnByMonth).reduce((s, v) => s + v, 0);

  // osoby z wolnymi mocami w którymś miesiącu (niedociążenie projektowe)
  const freeRows = data.employees.filter((e) =>
    e.by_month.some((c) => c.free_pln > 0.005),
  );
  const freeIds = new Set(freeRows.map((e) => e.employee_id));
  // osoby nieprojektowe w którymkolwiek miesiącu, których nie ma wyżej (obsługuje też mieszane:
  // nieprojektowy X mc + projektowy 1-2 mc bez wolnych mocy — inaczej wypadaliby z widoku)
  const nonProjRows = data.employees.filter(
    (e) =>
      !freeIds.has(e.employee_id) && e.by_month.some((c) => c.is_non_project),
  );

  const byKeyOf = (emp: ForecastEmployeeRow) => {
    const m: Record<string, ForecastCell> = {};
    emp.by_month.forEach((c) => {
      m[mkey(c.year, c.month)] = c;
    });
    return m;
  };

  const colCount = data.months.length + 1;

  return (
    <div className="bg-white dark:bg-gray-800 overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
      <table className="text-xs border-collapse mt-1 w-full">
        <thead>
          <tr className="border-b border-gray-200 dark:border-gray-700">
            <th className="sticky left-0 bg-white dark:bg-gray-800 text-left px-3 py-2 font-mono uppercase tracking-wider text-gray-500 min-w-[160px]">
              Pracownik
            </th>
            {data.months.map((m) => (
              <th
                key={mkey(m.year, m.month)}
                className="px-2 py-2 font-mono text-gray-500 dark:text-gray-400 min-w-[72px] text-center"
              >
                {MONTHS_SHORT[m.month - 1]}
                <div className="text-[10px] opacity-50">
                  {String(m.year).slice(2)}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {freeRows.map((emp) => {
            const byKey = byKeyOf(emp);
            return (
              <tr
                key={emp.employee_id}
                className="border-b border-gray-100 dark:border-gray-700/50"
              >
                <td className="sticky left-0 bg-white dark:bg-gray-800 px-3 py-1.5 text-gray-900 dark:text-gray-100 truncate max-w-[160px]">
                  {emp.employee_name}
                </td>
                {monthKeys.map((k) => {
                  const c = byKey[k];
                  if (c?.is_non_project) {
                    return (
                      <td key={k} className="px-2 py-1.5 text-center">
                        <span
                          className="text-gray-300 dark:text-gray-600"
                          title="Nieprojektowy w tym miesiącu — koszt rozłożony na zlecenia, nie generuje wolnych mocy"
                        >
                          –
                        </span>
                      </td>
                    );
                  }
                  const fp = c ? c.free_pln : 0;
                  const ff = c ? c.free_fraction : 0;
                  return (
                    <td
                      key={k}
                      className="px-2 py-1.5 text-center tabular-nums"
                    >
                      {fp > 0.005 ? (
                        <>
                          <div className="text-amber-600 dark:text-amber-400 font-medium">
                            {formatAmount(fp.toFixed(2))}
                          </div>
                          <div className="text-[9px] text-gray-400">
                            ({pct(ff)}%)
                          </div>
                        </>
                      ) : (
                        <span className="opacity-20">—</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}

          {/* osoby nieprojektowe — „–" w miesiącach nieprojektowych */}
          {nonProjRows.length > 0 && (
            <>
              <tr className="bg-gray-50 dark:bg-gray-800/60 border-y border-gray-200 dark:border-gray-700">
                <td
                  colSpan={colCount}
                  className="sticky left-0 px-3 py-1 text-[10px] font-mono uppercase tracking-wider text-gray-400"
                >
                  Nieprojektowi
                </td>
              </tr>
              {nonProjRows.map((emp) => {
                const byKey = byKeyOf(emp);
                return (
                  <tr
                    key={emp.employee_id}
                    className="border-b border-gray-100 dark:border-gray-700/50"
                  >
                    <td className="sticky left-0 bg-white dark:bg-gray-800 px-3 py-1.5 text-gray-700 dark:text-gray-300 truncate max-w-[160px]">
                      {emp.employee_name}
                    </td>
                    {monthKeys.map((k) => (
                      <td key={k} className="px-2 py-1.5 text-center">
                        {byKey[k]?.is_non_project ? (
                          <span
                            className="text-gray-400 dark:text-gray-500"
                            title="nieprojektowy"
                          >
                            –
                          </span>
                        ) : (
                          <span className="opacity-20">—</span>
                        )}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </>
          )}

          {freeRows.length === 0 && nonProjRows.length === 0 && (
            <tr>
              <td
                colSpan={colCount}
                className="py-8 text-center text-sm text-gray-400"
              >
                Brak wolnych mocy w tym oknie.
              </td>
            </tr>
          )}
        </tbody>
        <tfoot className="bg-gray-50 dark:bg-gray-800/60 border-t-2 border-gray-200 dark:border-gray-600">
          <tr>
            <td className="sticky left-0 bg-gray-50 dark:bg-gray-800/60 px-3 py-2 font-semibold text-gray-600 dark:text-gray-300">
              Razem wolne (zł)
            </td>
            {monthKeys.map((k) => (
              <td
                key={k}
                className="px-2 py-2 text-center tabular-nums font-bold text-amber-700 dark:text-amber-400"
              >
                {(freePlnByMonth[k] || 0) > 0.005
                  ? formatAmount((freePlnByMonth[k] || 0).toFixed(2))
                  : "—"}
              </td>
            ))}
          </tr>
        </tfoot>
      </table>
      <p className="text-xs text-gray-500 px-3 py-2">
        Łącznie:{" "}
        <b className="text-amber-700 dark:text-amber-400">
          {formatAmount(grandTotal.toFixed(2))} PLN
        </b>
      </p>
    </div>
  );
}

/* ── Modal edycji prognozy: kafelki po 10% (jak self-allocation) + tryb dokładny ── */
function ForecastEditModal({
  groups,
  employee,
  year,
  month,
  cell,
  prevFacts,
  onClose,
}: {
  groups: ForecastGroupRef[];
  employee: ForecastEmployeeRow;
  year: number;
  month: number;
  cell: ForecastCell;
  prevFacts: Record<string, number>;
  onClose: () => void;
}) {
  const employeeId = employee.employee_id;
  const employeeName = employee.employee_name;
  const setForecast = useSetGroupForecast();

  // Aktualny rozkład (group_id → ułamek) per miesiąc — do wyliczenia różnicy przy
  // zapisie na kilka miesięcy naraz (każdy miesiąc może mieć inny stan wyjściowy).
  const currentByMonth = useMemo(() => {
    const m: Record<string, Record<string, number>> = {};
    for (const c of employee.by_month) {
      const inner: Record<string, number> = {};
      for (const seg of c.segments) if (seg.group_id) inner[seg.group_id] = seg.fraction;
      m[mkey(c.year, c.month)] = inner;
    }
    return m;
  }, [employee.by_month]);

  // Miesiące, do których zastosujemy ustawienie. Start: tylko klikniety (kotwica).
  const [targetMonths, setTargetMonths] = useState<Set<string>>(
    () => new Set([mkey(year, month)]),
  );
  const editableKeys = useMemo(
    () =>
      employee.by_month
        .filter((c) => c.editable)
        .map((c) => mkey(c.year, c.month)),
    [employee.by_month],
  );
  function toggleMonth(key: string) {
    setTargetMonths((prev) => {
      const n = new Set(prev);
      if (n.has(key)) n.delete(key);
      else n.add(key);
      return n;
    });
  }
  // dokładny ułamek z faktu/prognozy per grupa (3 miejsca)
  const exactByGid = useMemo(() => {
    const m: Record<string, number> = {};
    for (const seg of cell.segments)
      if (seg.group_id) m[seg.group_id] = seg.fraction;
    return m;
  }, [cell.segments]);
  // initial: group_id → "0.400" (tylko grupy z niezerowym ułamkiem)
  const initial = useMemo(() => {
    const init: Record<string, string> = {};
    for (const g of groups) {
      const f = exactByGid[g.id] ?? 0;
      if (f > 0) init[g.id] = f.toFixed(3);
    }
    return init;
  }, [groups, exactByGid]);

  // Jeśli istniejące ułamki nie trzymają się siatki 10%, domyślnie pokaż tryb dokładny.
  const gridFriendly = useMemo(
    () =>
      Object.values(initial).every((v) => {
        const s = parseFloat(v) * 10;
        return Math.abs(s - Math.round(s)) <= 0.01;
      }),
    [initial],
  );
  const [mode, setMode] = useState<"tiles" | "precise">(
    gridFriendly ? "tiles" : "precise",
  );
  // tiles: group_id → "0.4"; precise: group_id → "40" (%)
  const [tileValues, setTileValues] = useState<Record<string, string>>(() => ({
    ...initial,
  }));
  const [pctValues, setPctValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      groups.map((g) => [
        g.id,
        String(Math.round((exactByGid[g.id] ?? 0) * 100)),
      ]),
    ),
  );

  // Wspólny ułamek finalny per grupa (zależnie od trybu), w postaci "0.000".
  const finalFractions = useMemo(() => {
    const out: Record<string, string> = {};
    for (const g of groups) {
      if (mode === "tiles") {
        out[g.id] = tileValues[g.id]
          ? parseFloat(tileValues[g.id]).toFixed(3)
          : "0.000";
      } else {
        out[g.id] = ((parseInt(pctValues[g.id] || "0", 10) || 0) / 100).toFixed(
          3,
        );
      }
    }
    return out;
  }, [groups, mode, tileValues, pctValues]);

  const totalFrac = Object.values(finalFractions).reduce(
    (s, v) => s + parseFloat(v),
    0,
  );
  const totalPct = Math.round(totalFrac * 100);
  const over = totalFrac > 1.0001;

  // Gdy przełączamy tryb, zsynchronizuj drugą reprezentację z aktualną.
  function switchMode(next: "tiles" | "precise") {
    if (next === mode) return;
    if (next === "precise") {
      setPctValues(
        Object.fromEntries(
          groups.map((g) => [
            g.id,
            String(Math.round(parseFloat(finalFractions[g.id]) * 100)),
          ]),
        ),
      );
    } else {
      const tv: Record<string, string> = {};
      for (const g of groups) {
        const f = parseFloat(finalFractions[g.id]);
        if (f > 0) tv[g.id] = f.toFixed(3);
      }
      setTileValues(tv);
    }
    setMode(next);
  }

  const save = () => {
    // Wysyłaj TYLKO grupy realnie zmienione w danym miesiącu (włącznie z wyzerowaniem
    // usuniętych → fraction "0"). Różnicę liczymy względem aktualnego stanu KAŻDEGO
    // wybranego miesiąca z osobna — po zapisie wszystkie mają identyczny rozkład.
    const entries: ForecastSetEntry[] = [];
    for (const key of targetMonths) {
      const [ty, tm] = key.split("-").map(Number);
      const cur = currentByMonth[key] ?? {};
      for (const g of groups) {
        const before = (cur[g.id] ?? 0).toFixed(3);
        const after = finalFractions[g.id] ?? "0.000";
        if (before !== after)
          entries.push({
            employee: employeeId,
            group: g.id,
            year: ty,
            month: tm,
            fraction: after,
          });
      }
    }
    if (entries.length === 0) {
      onClose();
      return;
    }
    setForecast.mutate(entries, { onSuccess: onClose });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-5 w-[34rem] max-h-[88vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 mb-1">
          <h2 className="font-bold text-gray-900 dark:text-gray-100">
            {employeeName}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 shrink-0"
          >
            <X size={18} />
          </button>
        </div>
        <p className="text-xs text-gray-500 mb-3">
          Prognoza na {MONTHS_SHORT[month - 1]} {year} — ile % etatu na którą
          grupę. Poniżej możesz zastosować ten sam rozkład do kilku miesięcy
          naraz.
        </p>

        {/* Przełącznik trybu */}
        <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 p-0.5 mb-4 text-xs">
          <button
            onClick={() => switchMode("tiles")}
            className={`px-3 py-1 rounded-md font-medium transition ${mode === "tiles" ? "bg-[var(--color-primary)] text-white" : "text-gray-500 hover:text-gray-700"}`}
          >
            Kafelki 10%
          </button>
          <button
            onClick={() => switchMode("precise")}
            className={`px-3 py-1 rounded-md font-medium transition ${mode === "precise" ? "bg-[var(--color-primary)] text-white" : "text-gray-500 hover:text-gray-700"}`}
          >
            Dokładny %
          </button>
        </div>

        {mode === "tiles" ? (
          <ForecastGridEditor
            groups={groups}
            value={tileValues}
            onChange={setTileValues}
            prevFacts={prevFacts}
          />
        ) : (
          <div className="flex flex-col gap-2 mb-3">
            {groups.map((g) => (
              <label key={g.id} className="flex items-center gap-2">
                <span className="flex-1 text-sm text-gray-700 dark:text-gray-300 truncate">
                  {g.short_name ? `${g.short_name} — ${g.name}` : g.name}
                </span>
                {prevFacts[g.id] !== undefined && (
                  <span
                    className="text-[10px] text-gray-400 tabular-nums w-10 text-right"
                    title="Poprzedni miesiąc"
                  >
                    ↩{pct(prevFacts[g.id])}%
                  </span>
                )}
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={pctValues[g.id] ?? ""}
                  onChange={(e) =>
                    setPctValues({ ...pctValues, [g.id]: e.target.value })
                  }
                  className="w-16 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm text-right bg-white dark:bg-gray-800 dark:text-gray-100"
                />
                <span className="text-xs text-gray-400">%</span>
              </label>
            ))}
          </div>
        )}

        <div
          className={`text-sm my-3 ${over ? "text-red-600 font-semibold" : totalPct === 100 ? "text-green-600" : "text-gray-500"}`}
        >
          Suma: {totalPct}%{" "}
          {over
            ? "— przeciążenie, popraw przed zapisem"
            : totalPct < 100
              ? `(wolne ${100 - totalPct}%)`
              : ""}
        </div>

        {/* Zastosuj rozkład do wybranych miesięcy (zablokowane = nieedytowalne) */}
        <div className="border-t border-gray-200 dark:border-gray-700 pt-3 mb-1">
          <div className="flex items-center justify-between gap-2 mb-2">
            <p className="text-[10px] uppercase tracking-widest text-gray-500">
              Zastosuj do miesięcy
            </p>
            <div className="flex items-center gap-2 text-xs">
              <button
                type="button"
                onClick={() => setTargetMonths(new Set(editableKeys))}
                className="text-[var(--color-primary)] hover:underline"
              >
                Wszystkie edytowalne
              </button>
              <span className="text-gray-300">·</span>
              <button
                type="button"
                onClick={() => setTargetMonths(new Set([mkey(year, month)]))}
                className="text-gray-500 hover:underline"
              >
                Tylko {MONTHS_SHORT[month - 1]} {String(year).slice(2)}
              </button>
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {employee.by_month.map((c) => {
              const key = mkey(c.year, c.month);
              const label = `${MONTHS_SHORT[c.month - 1]} ${String(c.year).slice(2)}`;
              if (!c.editable) {
                return (
                  <span
                    key={key}
                    title={
                      c.self_alloc_status !== "none"
                        ? "Przydział pracownika — nieedytowalny"
                        : "Miesiąc historyczny — tylko odczyt"
                    }
                    className="px-2 py-1 rounded text-xs tabular-nums bg-gray-50 dark:bg-gray-700/30 text-gray-300 dark:text-gray-600 line-through cursor-not-allowed"
                  >
                    {label}
                  </span>
                );
              }
              const selected = targetMonths.has(key);
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => toggleMonth(key)}
                  className={`px-2 py-1 rounded text-xs tabular-nums border transition ${
                    selected
                      ? "bg-[var(--color-primary)] text-white border-[var(--color-primary)]"
                      : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-[var(--color-primary)]"
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex items-center gap-2 justify-end mt-4">
          {targetMonths.size > 1 && (
            <span className="text-xs text-gray-500 mr-auto">
              Zapisze {targetMonths.size} miesięcy
            </span>
          )}
          <Button variant="secondary" onClick={onClose}>
            Anuluj
          </Button>
          <Button
            loading={setForecast.isPending}
            disabled={over || targetMonths.size === 0}
            onClick={save}
          >
            {targetMonths.size > 1
              ? `Zapisz prognozę (${targetMonths.size})`
              : "Zapisz prognozę"}
          </Button>
        </div>
      </div>
    </div>
  );
}
